"use client";

import Spinner from "@/components/icons/Spinner";
import { DialogOverlay } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { XMarkIcon } from "@heroicons/react/24/solid";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import React, { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import Button from "./Button";
interface useModalProp {
  isOpen?: boolean;
}

export const useModal = (props: useModalProp = { isOpen: false }) => {
  const [item, setItem] = useState<any>();
  const [isOpen, setOpen] = useState(props.isOpen);
  const [isLoading, setIsLoading] = useState(false);
  const [hideClose, setHideClose] = useState(false);
  const toggle = () => setOpen(!isOpen);
  const open = (item?: any) => {
    setOpen(true);
    if (item || item === null) {
      setItem(item);
    }
  };
  const close = () => setOpen(false);
  const setLoading = () => setIsLoading(true);
  const finishLoading = () => setIsLoading(false);

  return {
    isOpen,
    open,
    setOpen,
    toggle,
    close,
    isLoading,
    setLoading,
    finishLoading,
    item,
    hideClose,
    setHideClose,
  };
};

export interface ModalComponentProp extends ReturnType<typeof useModal> {
  children: React.ReactNode;
  className?: string;
  disabled?: boolean;
  blur?: boolean;
  drawer?: boolean;
  fullDrawer?: boolean;
}

const ModalComponent = ({
  isOpen,
  setOpen,
  children,
  disabled,
  isLoading,
  className,
  blur = false,
  hideClose,
  drawer = false,
  fullDrawer = false,
}: ModalComponentProp) => {
  // Optionally prevent scroll when dialog is open
  React.useEffect(() => {
    if (isOpen) {
      //...
    } else {
      setTimeout(() => {
        document.body.style.pointerEvents = "auto";
      }, 300);
    }
  }, [isOpen]);

  return (
    <DialogPrimitive.Root
      open={isOpen}
      onOpenChange={(v) => {
        if (disabled || isLoading || hideClose) return;
        setOpen(v);
      }}
    >
      <DialogPrimitive.DialogPortal data-slot="dialog-portal">
        <DialogOverlay
          className={cn(blur ? "backdrop-blur-sm bg-black/50" : "")}
        />
        <DialogPrimitive.Content
          data-slot="dialog-content"
          className={cn(
            "!pointer-events-none",
            "z-[999] fixed h-screen w-screen inset-0 overflow-y-auto overflow-x-hidden sid-scrollbar data-[state=open]:animate-in data-[state=closed]:animate-out",
            drawer
              ? "data-[state=open]:slide-in-from-bottom-5 lg:data-[state=open]:slide-in-from-bottom-0 data-[state=closed]:slide-out-to-bottom-2 lg:data-[state=closed]:slide-out-to-bottom-0 lg:data-[state=open]:zoom-in-95 lg:data-[state=closed]:zoom-out-95 data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
              : "data-[state=open]:zoom-in-95 data-[state=closed]:zoom-out-95 data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
          )}
        >
          <div
            className={cn(
              "pointer-events-none",
              "flex flex-col w-screen min-h-screen",
              drawer ? "justify-end sm:justify-center" : "justify-center p-3"
            )}
          >
            <div
              className={cn(
                "pointer-events-auto",
                "relative gap-4 p-3 mx-auto w-full border shadow-lg duration-200 bg-background sm:max-w-lg",
                drawer
                  ? "pb-16 rounded-t-lg sm:rounded-lg sm:pb-3"
                  : "rounded-lg",
                className
              )}
            >
              {children}
            </div>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.DialogPortal>
    </DialogPrimitive.Root>
  );
  /*
  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className={cn("overflow-y-auto fixed inset-0 z-50")}
        style={{ zIndex: 9999999 }}
        onClose={closeModal}
      >
        {blur && (
          <div className="fixed bg-black/10 backdrop-blur-sm w-screen h-[100dvh]" />
        )}
        <div
          className={cn(
            drawer
              ? "flex flex-col justify-end items-center text-center min-h-[100dvh] sm:p-4 sm:justify-center"
              : "flex relative flex-col justify-center items-center p-4 w-full min-h-[100dvh]"
          )}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-in-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in-out duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-black/40" />
          </Transition.Child>

          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom={cn(
              "opacity-0",
              drawer
                ? "translate-y-10 sm:translate-y-0 sm:scale-90"
                : "scale-90"
            )}
            enterTo={cn(
              "opacity-100",
              drawer ? "translate-y-0 sm:scale-100" : "scale-100"
            )}
            leave="ease-in duration-100"
            leaveFrom={cn(
              "opacity-100",
              drawer ? "translate-y-0 sm:scale-100" : "scale-100"
            )}
            leaveTo={cn(
              "opacity-0",
              drawer
                ? "translate-y-10 sm:translate-y-0 sm:scale-90"
                : "scale-90"
            )}
          >
            <div
              className={cn(
                "z-10 p-4 transition-all transform bg-background dark:border",
                drawer
                  ? [
                      "w-full text-left align-middle sm:shadow-xl sm:rounded-lg sm:max-w-md",
                      fullDrawer ? "min-h-[100dvh] sm:min-h-0" : "rounded-t-lg",
                    ]
                  : "w-full max-w-md text-left align-middle rounded-lg shadow-xl",
                className
              )}
            >
              {children}
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
  */
};

export const ConfirmModal = ({ title, content, onOk, modalProp }) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);

  const onConfirm = async () => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      if (await onOk()) {
        modalProp.close();
      }
    } catch (e) {
      //...
    }
    setIsLoading(false);
  };

  return (
    <ModalComponent {...modalProp} drawer className="sm:!max-w-[28rem]">
      <div className="flex items-center">
        <h1 className="flex-1 font-montserrat font-semibold text-xl">
          {title}
        </h1>
        <button className="p-1" onClick={modalProp.close}>
          <XMarkIcon className="w-6" />
        </button>
      </div>
      <div className="py-3">{content}</div>
      <div className={"flex gap-2 justify-end mt-2 mb-10 sm:mb-0"}>
        <Button
          variant="outline"
          className="w-full sm:w-auto sm:!min-w-[6rem]"
          disabled={isLoading}
          onClick={modalProp.close}
        >
          <Trans i18nKey="cancel">Cancel</Trans>
        </Button>
        <Button
          variant="blue"
          className="w-full sm:w-auto sm:!min-w-[6rem]"
          onClick={onConfirm}
          disabled={isLoading}
        >
          {isLoading && <Spinner className="animate-spin" />}
          <Trans i18nKey="yes">Yes</Trans>
        </Button>
      </div>
    </ModalComponent>
  );
};

export const ModalHeader = ({
  children,
  close,
  className = "",
  disabled = false,
  hideClose,
}: ReturnType<typeof useModal> & {
  disabled?: boolean;
  className?: string;
  children?: React.ReactNode;
}) => {
  return (
    <div className="flex items-center mb-3">
      <DialogPrimitive.Title
        data-slot="dialog-title"
        className={cn(
          "flex-1 text-xl font-semibold font-montserrat",
          className
        )}
      >
        {children}
      </DialogPrimitive.Title>
      {!hideClose && (
        <button onClick={close} disabled={disabled}>
          <XMarkIcon className="w-6" />
        </button>
      )}
    </div>
  );
};

export default ModalComponent;
